import { formatBraceBasedCode, formatBasicIndentation } from "./baseFormatters";

/**
 * Python-specific formatting function using autopep8-style formatting
 * @param {string} code - Python code to format
 * @returns {string} Formatted Python code
 */
export const formatPythonCode = (code) => {
  console.log("Formatting Python code...");
  const lines = code.split("\n");
  const formattedLines = [];
  let indentLevel = 0;
  const indentSize = 4; // Python standard is 4 spaces

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Skip empty lines but preserve them
    if (trimmedLine === "") {
      formattedLines.push("");
      continue;
    }

    // Handle dedent keywords
    if (
      trimmedLine.startsWith("else:") ||
      trimmedLine.startsWith("elif ") ||
      trimmedLine.startsWith("except") ||
      trimmedLine.startsWith("finally:") ||
      trimmedLine.startsWith("except:") ||
      trimmedLine.startsWith("except ")
    ) {
      indentLevel = Math.max(0, indentLevel - 1);
    }

    // Add proper indentation
    const indent = " ".repeat(indentLevel * indentSize);
    formattedLines.push(indent + trimmedLine);

    // Handle indent keywords
    if (
      trimmedLine.endsWith(":") &&
      !trimmedLine.startsWith("#") &&
      !trimmedLine.startsWith('"""') &&
      !trimmedLine.startsWith("'''")
    ) {
      indentLevel++;
    }
  }

  const result = formattedLines.join("\n");
  console.log("Python formatting completed");
  return result;
};

/**
 * JavaScript/TypeScript formatter with basic but effective formatting
 * @param {string} code - JavaScript/TypeScript code to format
 * @returns {string} Formatted code
 */
export const formatJavaScriptCode = (code) => {
  console.log("Formatting JavaScript/TypeScript code...");
  const lines = code.split("\n");
  const formattedLines = [];
  let indentLevel = 0;
  const indentSize = 2;

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine === "") {
      formattedLines.push("");
      continue;
    }

    // Handle closing braces and brackets
    if (trimmedLine.startsWith("}") || trimmedLine.startsWith("]")) {
      indentLevel = Math.max(0, indentLevel - 1);
    }

    const indent = " ".repeat(indentLevel * indentSize);
    formattedLines.push(indent + trimmedLine);

    // Handle opening braces and brackets
    if (trimmedLine.endsWith("{") || trimmedLine.endsWith("[")) {
      indentLevel++;
    }
  }

  const result = formattedLines.join("\n");
  console.log("JavaScript/TypeScript formatting completed");
  return result;
};

/**
 * Basic Java formatter
 * @param {string} code - Java code to format
 * @returns {string} Formatted Java code
 */
export const formatJavaCode = (code) => {
  const lines = code.split("\n");
  const formattedLines = [];
  let indentLevel = 0;
  const indentSize = 4; // Java standard is 4 spaces (Oracle/Google style guide)

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine === "") {
      formattedLines.push("");
      continue;
    }

    // Handle closing braces
    if (trimmedLine.startsWith("}")) {
      indentLevel = Math.max(0, indentLevel - 1);
    }

    const indent = " ".repeat(indentLevel * indentSize);
    formattedLines.push(indent + trimmedLine);

    // Handle opening braces
    if (trimmedLine.endsWith("{")) {
      indentLevel++;
    }
  }

  return formattedLines.join("\n");
};

/**
 * Basic C/C++ formatter
 * @param {string} code - C/C++ code to format
 * @returns {string} Formatted code
 */
export const formatCCode = (code) => {
  const lines = code.split("\n");
  const formattedLines = [];
  let indentLevel = 0;
  const indentSize = 4; // C/C++ standard is 4 spaces (common industry practice)

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine === "") {
      formattedLines.push("");
      continue;
    }

    // Handle closing braces
    if (trimmedLine.startsWith("}")) {
      indentLevel = Math.max(0, indentLevel - 1);
    }

    const indent = " ".repeat(indentLevel * indentSize);
    formattedLines.push(indent + trimmedLine);

    // Handle opening braces
    if (trimmedLine.endsWith("{")) {
      indentLevel++;
    }
  }

  return formattedLines.join("\n");
};

/**
 * C# formatter
 * @param {string} code - C# code to format
 * @returns {string} Formatted C# code
 */
export const formatCSharpCode = (code) => {
  return formatBraceBasedCode(code, 4); // C# typically uses 4 spaces
};

/**
 * Go formatter
 * @param {string} code - Go code to format
 * @returns {string} Formatted Go code
 */
export const formatGoCode = (code) => {
  return formatBraceBasedCode(code, 4); // Go uses tabs, but since we're using spaces, use 4 spaces for consistency
};

/**
 * Rust formatter
 * @param {string} code - Rust code to format
 * @returns {string} Formatted Rust code
 */
export const formatRustCode = (code) => {
  return formatBraceBasedCode(code, 4); // Rust uses 4 spaces
};

/**
 * PHP formatter
 * @param {string} code - PHP code to format
 * @returns {string} Formatted PHP code
 */
export const formatPHPCode = (code) => {
  const lines = code.split("\n");
  const formattedLines = [];
  let indentLevel = 0;
  const indentSize = 4;

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine === "") {
      formattedLines.push("");
      continue;
    }

    // Handle closing braces and PHP tags
    if (trimmedLine.startsWith("}") || trimmedLine === "?>") {
      indentLevel = Math.max(0, indentLevel - 1);
    }

    const indent = " ".repeat(indentLevel * indentSize);
    formattedLines.push(indent + trimmedLine);

    // Handle opening braces and PHP tags
    if (trimmedLine.endsWith("{") || trimmedLine.startsWith("<?php")) {
      indentLevel++;
    }
  }

  return formattedLines.join("\n");
};

/**
 * Ruby formatter
 * @param {string} code - Ruby code to format
 * @returns {string} Formatted Ruby code
 */
export const formatRubyCode = (code) => {
  const lines = code.split("\n");
  const formattedLines = [];
  let indentLevel = 0;
  const indentSize = 2;

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine === "") {
      formattedLines.push("");
      continue;
    }

    // Handle dedent keywords
    if (
      trimmedLine.startsWith("end") ||
      trimmedLine.startsWith("elsif") ||
      trimmedLine.startsWith("else") ||
      trimmedLine.startsWith("rescue") ||
      trimmedLine.startsWith("ensure")
    ) {
      indentLevel = Math.max(0, indentLevel - 1);
    }

    const indent = " ".repeat(indentLevel * indentSize);
    formattedLines.push(indent + trimmedLine);

    // Handle indent keywords
    if (
      trimmedLine.match(/\b(if|unless|while|until|for|def|class|module|begin|do|case)\b/) ||
      trimmedLine.endsWith("do")
    ) {
      indentLevel++;
    }
  }

  return formattedLines.join("\n");
};

/**
 * Swift formatter
 * @param {string} code - Swift code to format
 * @returns {string} Formatted Swift code
 */
export const formatSwiftCode = (code) => {
  return formatBraceBasedCode(code, 4); // Swift uses 4 spaces
};

/**
 * Kotlin formatter
 * @param {string} code - Kotlin code to format
 * @returns {string} Formatted Kotlin code
 */
export const formatKotlinCode = (code) => {
  return formatBraceBasedCode(code, 4); // Kotlin uses 4 spaces
};

/**
 * Scala formatter
 * @param {string} code - Scala code to format
 * @returns {string} Formatted Scala code
 */
export const formatScalaCode = (code) => {
  return formatBraceBasedCode(code, 2); // Scala uses 2 spaces
};

/**
 * SQL formatter (basic)
 * @param {string} code - SQL code to format
 * @returns {string} Formatted SQL code
 */
export const formatSQLCode = (code) => {
  const lines = code.split("\n");
  const formattedLines = [];
  let indentLevel = 0;
  const indentSize = 2;

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine === "") {
      formattedLines.push("");
      continue;
    }

    // Handle closing parentheses
    if (trimmedLine.startsWith(")")) {
      indentLevel = Math.max(0, indentLevel - 1);
    }

    const indent = " ".repeat(indentLevel * indentSize);
    formattedLines.push(indent + trimmedLine);

    // Handle opening parentheses and SQL keywords that increase indent
    if (trimmedLine.endsWith("(") || trimmedLine.match(/\b(SELECT|FROM|WHERE|JOIN|GROUP BY|ORDER BY)\b/i)) {
      indentLevel++;
    }
  }

  return formattedLines.join("\n");
};

